import { RequestPersonalInfo, FHService, ProvinceFilterComponent, ModalService } from 'npx-family-happy-common';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { UserStorageService } from 'src/app/authentication/user-storage/user-storage.service';
import { environment } from 'src/environments/environment';
import { Loader } from '@googlemaps/js-api-loader';
import { Observable, Subject, combineLatest, debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs';
import { ServiceServices } from 'src/app/backoffice/services/service-services/service.services';
import { CommonModule } from '@angular/common';
import { cellFormatter, emailFormatter } from 'src/app/shared/utils.functions';
import { User } from 'src/app/shared/models/user.model';
import { RequestService } from '../request-service/request.service';
import { NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { UserManagementService } from 'src/app/backoffice/user-management/user-management-service/user-management.service';
import { UserAddress } from 'src/app/shared/models/userAddress.interface';
import { UserFiscalInfo } from 'src/app/shared/models/userFiscalInfo.interface';
import { UserWorkInfo } from 'src/app/shared/models/userWorkInfo.interface';
import { ContactSetting } from 'src/app/shared/models/contactSetting.interface';
import { BUSINESS_LINE_OPTIONS, SelectValuesInterface } from 'src/app/shared/models/businessLine-model';

@Component({
  selector: 'app-request-personal-info',
  templateUrl: './request-personal-info.component.html',
  styleUrls: ['./request-personal-info.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ProvinceFilterComponent, NgbTypeaheadModule]
})
export class RequestPersonalInfoComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() inputData!: RequestPersonalInfo;
  @Input() requestID!: number;
  @Input() requestCategory!: string;
  @Output() dataUpdated = new EventEmitter<{ requestID: number, requestPersonalInfo: RequestPersonalInfo, oldAddress: string }>();
  @Output() addressUpdated = new EventEmitter<any>();


  form = new UntypedFormGroup({
    id: new UntypedFormControl(null, Validators.required),
    operator: new UntypedFormControl('', Validators.required),
    customer: new UntypedFormControl('', Validators.required),
    service: new UntypedFormControl('', Validators.required),
    contactType: new UntypedFormControl('', [Validators.required]),
    contactDate: new UntypedFormControl('', [Validators.required]),
    contactHours: new UntypedFormControl('', Validators.required),
    contactPreference: new UntypedFormControl('', Validators.required),
    phoneDate: new UntypedFormControl('', Validators.required),
    parentName: new UntypedFormControl('', Validators.required),
    parentSurname: new UntypedFormControl('', Validators.required),
    parentEmail: new UntypedFormControl('', Validators.required),
    parentPhone: new UntypedFormControl('', Validators.required),
    parentCity: new UntypedFormControl('', Validators.required),
    parentZone: new UntypedFormControl('', Validators.required),
    parentAddress: new UntypedFormControl('', Validators.required),
    parentCap: new UntypedFormControl('', [Validators.pattern(new RegExp('^\\d+$'))]),
    provincia: new UntypedFormControl('TO', Validators.required),
    expDate: new UntypedFormControl('', Validators.required),
    manualExpDate: new UntypedFormControl('', Validators.required),
    subscription: new UntypedFormControl(''),
    manualSubscription: new UntypedFormControl(''),
    referral: new UntypedFormControl(''),
    message: new UntypedFormControl('', Validators.required),
    company: new UntypedFormControl(false),
    companySize: new UntypedFormControl(''),
    welfareCompany: new UntypedFormControl(''),
    requestCategory: new UntypedFormControl(''),
    parentFiscalCode: new UntypedFormControl(''),
    serviceLinked: new UntypedFormControl(),
    latitude: new UntypedFormControl(null),
    longitude: new UntypedFormControl(null)
  })
  showOperatorSwap = false;
  servicesAvailable: FHService[] = [];
  customerTypesAvailable: string[] = [];

  private currentUser!: User | undefined;
  private unsubscribe = new Subject<void>();
  private oldAddress!: string;
  selectValue!:Array<SelectValuesInterface>;

  get id() {
    return this.form.get('id')?.value;
  }

  constructor(
    private userStorage: UserStorageService,
    private serviceService: ServiceServices,
    private requestService: RequestService,
    private modalService: ModalService,
    private userService: UserManagementService
  ) {
    this.selectValue = BUSINESS_LINE_OPTIONS;
   }

  ngOnInit(): void {
    this.userStorage.getUser().pipe(takeUntil(this.unsubscribe)).subscribe(user => {
      this.currentUser = user;
    });

    // Initial load of services based on current values
    this.loadServicesAndCustomerTypes(this.inputData.customer, this.inputData.requestCategory);
    this.loadCustomerTypes(this.inputData.requestCategory);

    // Watch for changes in customer and business line
    this.form.get('customer')?.valueChanges.pipe(
      takeUntil(this.unsubscribe)
    ).subscribe(customerType => {
      const businessLine = this.form.get('requestCategory')?.value;
      this.loadServicesAndCustomerTypes(customerType, businessLine);
    });

    this.form.get('requestCategory')?.valueChanges.pipe(
      takeUntil(this.unsubscribe)
    ).subscribe(businessLine => {
      const customerType = this.form.get('customer')?.value;
      this.loadServicesAndCustomerTypes(customerType, businessLine);
      this.loadCustomerTypes(businessLine);
    });

    this.initForm();
    this.form.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      const requestID = value.id;
      delete value.id;
      const requestPersonalInfo: RequestPersonalInfo = {
        ...value,
        parentEmail: value.parentEmail && value.parentEmail.email ? emailFormatter(value.parentEmail.email) : emailFormatter(value.parentEmail),
        parentPhone: value.parentPhone ? cellFormatter(value.parentPhone) : null,
        expDate: value.expDate !== null && value.expDate !== '' ? new Date(value.expDate) : null,
        manualExpDate: value.manualExpDate !== null && value.manualExpDate !== '' ? new Date(value.manualExpDate) : null,
      }
      if (this.form.get('parentAddress')!.value === '') {
        requestPersonalInfo.latitude = 0
        requestPersonalInfo.longitude = 0
      }
      const oldAddress = this.oldAddress
      this.dataUpdated.emit({
        requestID,
        requestPersonalInfo,
        oldAddress
      });
    })
    // Patch request if serviceLinked is not available
    if (this.inputData.serviceLinked === null) {
      this.serviceService.getAllServices(0, 10, { searchKey: this.inputData.service, businessLine: this.inputData.requestCategory, customerType: this.inputData.customer, disabled: null }).subscribe((services) => {
        // Find the service with the exact service name
        const service = services.content.find(item => item.name === this.inputData.service);
        if (service) {
          alert(`Durante l'ultimo aggiornamento, è stata cambiata la logica con cui vengono gestiti i servizi collegati alle richieste. Quando si apre la richiesta, se il servizio non è ancora stato collegato correttamente, avrai questo avviso e ti sarà chiesto di salvare le modifiche. Effettua il salvataggio e questo alert scomparirà.`)
          this.inputData.serviceLinked = service;
          this.form.get('serviceLinked')?.setValue(service);
        }
      })
    }
  }

  ngAfterViewInit(): void {
    this.loadGoogleMaps(document.getElementById('indirizzogenitore') as HTMLInputElement);
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  operatorSwap() {
    if (this.currentUser) {
      this.requestService.changeOperator(this.requestID, this.currentUser.uid).subscribe((request) => {
        this.form.get('operator')?.setValue(this.currentUser?.name);
        this.showOperatorSwap = false;
        this.modalService.showSuccessMessage('Hai preso in carico questa richiesta.');
      });
    }
  }

  updateProvincia(newValue: string) {
    this.form.get('provincia')?.setValue(newValue);
    this.form.markAllAsTouched();
    this.form.get('provincia')?.markAsDirty();
  }

  loadGoogleMaps(input: HTMLInputElement) {
    input.addEventListener('change', () => {
      input.value = '';
      this.form.get('parentAddress')?.setValue('', { emitEvent: false });
    });

    const loader = new Loader({ apiKey: environment.googleMapsAPIKey, version: "weekly", libraries: ["places"], language: 'it-IT' }).load().then((google) => {
      const autocomplete = new google.maps.places.Autocomplete(input);

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        let formAddress = this.form.get('parentAddress');

        formAddress?.setValue(place.formatted_address);

        // Compile other address fields according to place object
        place.address_components?.forEach((component: any) => {
          if (component.types.includes('locality')) {
            this.form.get('parentCity')?.setValue(component.long_name);
          }
          if (component.types.includes('postal_code')) {
            this.form.get('parentCap')?.setValue(component.long_name);
          }
        });
        this.addressUpdated.emit(place.formatted_address)
      })
    });
  }

  selectService(event: Event) {
    const selectedServiceName = (event.target as HTMLSelectElement).value;
    const service = this.servicesAvailable.find(service => service.name === selectedServiceName);
    this.form.get('serviceLinked')?.setValue(service);
  }

  /* User search ahead */
  userSearch = (text$: Observable<string>) => {
    return text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      switchMap((searchKey: string) => {
        searchKey = emailFormatter(searchKey);
        return this.userService.getUsersList(0, 5, { email: searchKey }).pipe(takeUntil(this.unsubscribe), map((page) => page.content));
      }),
    );
  }

  formatter = (user: User) => {
    return user.email;
  }

  onUserSelected(event: any) {
    const customer = event.item as User;
    const address: UserAddress | undefined = customer.addresses?.find(item => item.isDefault);
    const fiscalInfo: UserFiscalInfo | null = customer.fiscalInfo;
    const workInfo: UserWorkInfo | null = customer.workInfo;
    const contactSettings: ContactSetting | null = customer.contactSettings;
    // Populate form with customer data
    this.form.setValue({
      ...this.form.value,
      parentName: customer.personalInfo?.name,
      parentSurname: customer.personalInfo?.surname,
      parentEmail: customer.personalInfo?.email,
      parentPhone: customer.personalInfo?.phone,
      parentCity: address ? address.city : null,
      parentZone: address ? address.zone : null,
      parentAddress: address ? address.street : null,
      parentCap: address ? address.postalCode : null,
      provincia: address ? address.province : null,
      contactHours: contactSettings?.contactHours,
      contactPreference: contactSettings?.contactPreference,
      referral: contactSettings?.referral,
      company: workInfo?.company,
      companySize: workInfo?.companySize,
      welfareCompany: workInfo?.welfareCompany,
      parentFiscalCode: fiscalInfo?.fiscalCode,
      latitude: address ? address.latitude : null,
      longitude: address ? address.longitude : null,
      id: typeof this.requestID === 'number' ? this.requestID : null,
    })
  }

  private loadServicesAndCustomerTypes(customerType: string, businessLine: string) {
    this.serviceService.getAllServices(0, 50, {
      searchKey: null,
      businessLine,
      customerType,
      disabled: false
    }).pipe(takeUntil(this.unsubscribe)).subscribe((result) => {
      this.servicesAvailable = result.content;
    });
  }

  // Customer types depend only on the business line, so they must not be filtered by the selected customer type
  private loadCustomerTypes(businessLine: string) {
    this.serviceService.getAllServices(0, 200, {
      searchKey: null,
      businessLine,
      customerType: null,
      disabled: false
    }).pipe(takeUntil(this.unsubscribe)).subscribe((result) => {
      this.customerTypesAvailable = [...new Set(result.content.map(item => item.customerType))]
        .filter(type => type !== 'CAREGIVER');
    });
  }

  private initForm() {
    return this.userService.getUsersList(0, 1, { email: this.inputData.parentEmail })
      .pipe(takeUntil(this.unsubscribe), map((page) => page.content))
      .subscribe((users) => {
        let user: User | null = null;
        if (users.length > 0) {
          user = users[0];
        }
        this.form.setValue({
          ...this.inputData,
          parentEmail: user,
          parentPhone: this.inputData.parentPhone ? emailFormatter(this.inputData.parentPhone) : null,
          id: this.requestID,
          expDate: this.inputData.expDate ? this.inputData.expDate.toISOString().split('T')[0] : '',
          manualExpDate: this.inputData.manualExpDate ? this.inputData.manualExpDate.toISOString().split('T')[0] : ''
        }, { emitEvent: false });


        this.checkOperatorSwap();
        this.oldAddress = this.inputData.parentAddress;
      });
  }

  private checkOperatorSwap() {
    if (this.currentUser?.name !== this.inputData.operator && this.id !== null) {
      this.showOperatorSwap = true;
    } else {
      this.showOperatorSwap = false;
    }
  }
}
